import React, { useCallback, useEffect, useRef } from 'react';
import {
  InboundDetailList,
  Props as InboundDetailListProps,
} from './inbound-detail-list';
import { OrderMetadata } from './types';
import { useOrderInboundDetailStore } from './store/order-inbound-detail-store';

interface Props
  extends Omit<InboundDetailListProps, 'updateItemNotes' | 'updateQuantity'> {
  isProcessingReply?: boolean;
  orderMetadata: OrderMetadata[];
  rehydrateKey?: number;
  showTranslation?: boolean;
}

export const InboundDetailForm: React.FC<Props> = ({
  dispatch,
  isProcessingReply = false,
  orderMetadata,
  rehydrateKey = 0,
  showTranslation,
  ...detailListProps
}) => {
  const lastHydratedKeyRef = useRef(-1);
  const { isProcessing, ...restDetailListProps } = detailListProps;

  const setSelectedInputUUID = useOrderInboundDetailStore(
    (state) => state.actions.setSelectedInputUUID,
  );
  const clearPendingItemNotes = useOrderInboundDetailStore(
    (state) => state.actions.clearPendingItemNotes,
  );

  const updateItemNotes = useCallback(
    (searchInputUUID: string, itemNotes: string | null) => {
      dispatch({
        payload: { itemNotes, searchInputUUID },
        type: 'UPDATE_ITEM_NOTES',
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (lastHydratedKeyRef.current === rehydrateKey) {
      return;
    }
    lastHydratedKeyRef.current = rehydrateKey;
    clearPendingItemNotes();

    // For non email thread reply processing, clear stale items from the
    // previous restaurant so the loading skeleton condition can fire.
    // For reply processing, keep existing chain items visible.
    dispatch({
      payload: {
        orderMetadata:
          isProcessing && !isProcessingReply ? [] : (orderMetadata ?? []),
      },
      type: 'HYDRATE',
    });

    // TODO: find a better place to do this on initial hydrate
    if (!(isProcessing && !isProcessingReply) && orderMetadata.length > 0) {
      const firstOrderMetadata = orderMetadata[0];
      if (firstOrderMetadata.searchInputs.length > 0) {
        setSelectedInputUUID(firstOrderMetadata.searchInputs[0].uuid);
      }
    }
  }, [
    orderMetadata,
    rehydrateKey,
    dispatch,
    setSelectedInputUUID,
    clearPendingItemNotes,
    isProcessing,
    isProcessingReply,
  ]);

  return (
    <InboundDetailList
      {...restDetailListProps}
      dispatch={dispatch}
      isProcessing={isProcessing}
      orderMetadata={orderMetadata}
      showTranslation={showTranslation}
      updateItemNotes={updateItemNotes}
      updateQuantity={(searchInputUUID, quantity) => {
        dispatch({
          payload: {
            quantity,
            searchInputUUID,
          },
          type: 'UPDATE_QUANTITY',
        });
      }}
    />
  );
};
